import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createExpense,
  deleteExpense,
  getExpenseById,
  getExpenseFormData,
  getExpenses,
  updateExpense,
} from "../../services/expenseService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const SHOP_ROLES = [
  "gerant_magasin",
]


const ORGANIZATION_ROLES = [
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "stock_maison_djo",
  "gestionnaire_stock_maison_djo",
  "admin_depot_relais",
  "gestionnaire_depot_relais",
  "stock_depot_relais",
]


const ADMIN_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_operations",
]


const defaultFormData = {
  company_id: 1,
  store_id: "",
  user_id: "",
  expense_date: "",
  expense_type: "",
  amount: "",
  description: "",
  status: "confirmed",
  external_reference: "",
  created_by: "",
}


const expenseTypeLabels = {
  rent: "Loyer",
  transport: "Transport",
  fuel: "Carburant",
  salary: "Salaire",
  maintenance: "Maintenance",
  supplies: "Fournitures",
  communication: "Communication",
  utility: "Eau / Électricité",
  security: "Sécurité",
  cleaning: "Nettoyage",
  other: "Autre",
}


const statusLabels = {
  confirmed: "Confirmée",
  pending: "En attente",
  cancelled: "Annulée",
}


// =========================================================
// HELPERS
// =========================================================

function getLocalNumber(key) {
  const value =
    localStorage.getItem(key)

  if (!value) {
    return ""
  }

  const parsed =
    Number(value)

  return Number.isNaN(parsed)
    ? ""
    : parsed
}


function getSession() {
  return {
    userId:
      getLocalNumber(
        "kongosys_user_id"
      ),

    fullName:
      localStorage.getItem(
        "kongosys_full_name"
      ) || "Utilisateur",

    role:
      localStorage.getItem(
        "kongosys_role"
      ) || "",

    companyId:
      getLocalNumber(
        "kongosys_company_id"
      ) || 1,

    organizationId:
      getLocalNumber(
        "kongosys_organization_id"
      ),

    organizationName:
      localStorage.getItem(
        "kongosys_organization_name"
      ) || "",

    organizationCode:
      localStorage.getItem(
        "kongosys_organization_code"
      ) || "",

    storeId:
      getLocalNumber(
        "kongosys_store_id"
      ),

    storeName:
      localStorage.getItem(
        "kongosys_store_name"
      ) || "",

    storeCode:
      localStorage.getItem(
        "kongosys_store_code"
      ) || "",
  }
}


function isShopUser(role) {
  return SHOP_ROLES.includes(
    role
  )
}


function isOrganizationUser(role) {
  return ORGANIZATION_ROLES.includes(
    role
  )
}


function isAdminUser(role) {
  return ADMIN_ROLES.includes(
    role
  )
}


function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatDate(value) {
  if (!value) {
    return "-"
  }

  try {
    return new Date(
      value
    ).toLocaleDateString(
      "fr-FR"
    )
  } catch {
    return "-"
  }
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-"
  }

  return date.toLocaleString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
}


function formatDateForInput(value) {
  if (!value) {
    return ""
  }

  try {
    const date =
      new Date(value)

    const offset =
      date.getTimezoneOffset()

    const localDate =
      new Date(
        date.getTime() -
          offset * 60000
      )

    return localDate
      .toISOString()
      .slice(
        0,
        16
      )
  } catch {
    return ""
  }
}


function getNowForInput() {
  const date =
    new Date()

  const offset =
    date.getTimezoneOffset()

  const localDate =
    new Date(
      date.getTime() -
        offset * 60000
    )

  return localDate
    .toISOString()
    .slice(
      0,
      16
    )
}


function getExpenseTypeLabel(value) {
  return (
    expenseTypeLabels[
      value
    ] ||
    value ||
    "-"
  )
}


function getStatusConfig(value) {
  if (
    value === "confirmed"
  ) {
    return {
      label: "Confirmée",
      color: "success",
    }
  }

  if (
    value === "pending"
  ) {
    return {
      label: "En attente",
      color: "warning",
    }
  }

  if (
    value === "cancelled"
  ) {
    return {
      label: "Annulée",
      color: "error",
    }
  }

  return {
    label:
      statusLabels[value] ||
      value ||
      "-",

    color: "default",
  }
}


function getStatusChip(value) {
  const config =
    getStatusConfig(value)

  return (
    <Chip
      size="small"
      color={config.color}
      label={config.label}
      sx={{
        height: 24,
        fontWeight: 900,
      }}
    />
  )
}


function getExpenseRowClass(status) {
  if (
    status === "confirmed"
  ) {
    return "expense-row expense-row--confirmed"
  }

  if (
    status === "pending"
  ) {
    return "expense-row expense-row--pending"
  }

  if (
    status === "cancelled"
  ) {
    return "expense-row expense-row--cancelled"
  }

  return "expense-row expense-row--default"
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.1,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function ExpenseMobileCard({
  row,
  shopUser,
  onEdit,
  onDelete,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          row.status ===
          "confirmed"
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status ===
              "pending"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status ===
              "cancelled"
            ? "inset 4px 0 0 #ef4444, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #cbd5e1, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {row.expense_code ||
                  `DEP-${row.id}`}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {formatDateTime(
                  row.expense_date
                )}
              </Typography>
            </Box>

            {getStatusChip(
              row.status
            )}
          </Box>


          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems: "center",

              gap: 1,
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={getExpenseTypeLabel(
                row.expense_type
              )}
              sx={{
                maxWidth: "65%",

                "& .MuiChip-label":
                  {
                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  },
              }}
            />

            <Typography
              fontWeight={900}
              color="#0f172a"
              sx={{
                fontSize:
                  "1.05rem",
              }}
            >
              {formatAmount(
                row.amount
              )}
            </Typography>
          </Box>


          <Paper
            variant="outlined"
            sx={{
              p: 1,

              borderRadius: 2.5,

              bgcolor: "#fbfdff",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Shop
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
              sx={{
                mt: 0.15,
              }}
            >
              {row.store_name ||
                "-"}
            </Typography>

            {!shopUser &&
            row.organization_name ? (
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {
                  row.organization_name
                }
              </Typography>
            ) : null}
          </Paper>


          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Utilisateur
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
            >
              {row.user_name ||
                "-"}
            </Typography>
          </Box>


          {row.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflowWrap:
                  "anywhere",
              }}
            >
              {row.description}
            </Typography>
          ) : null}


          {row.external_reference ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflowWrap:
                  "anywhere",
              }}
            >
              Réf. :{" "}
              {
                row.external_reference
              }
            </Typography>
          ) : null}


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  shopUser
                    ? "1fr"
                    : "repeat(2, minmax(0, 1fr))",
              },

              gap: 0.7,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                onEdit(
                  row.id
                )
              }
            >
              Modifier
            </Button>


            {!shopUser && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() =>
                  onDelete(
                    row.id
                  )
                }
              >
                Supprimer
              </Button>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function ExpensesPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // SESSION
  // =======================================================

  const session =
    getSession()

  const shopUser =
    isShopUser(
      session.role
    )

  const organizationUser =
    isOrganizationUser(
      session.role
    )

  const adminUser =
    isAdminUser(
      session.role
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    expenses,
    setExpenses,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    users,
    setUsers,
  ] = useState([])

  const [
    expenseTypes,
    setExpenseTypes,
  ] = useState([])

  const [
    statuses,
    setStatuses,
  ] = useState([])


  // =======================================================
  // UI
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedExpense,
    setSelectedExpense,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    typeFilter,
    setTypeFilter,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")

  const [
    dateFrom,
    setDateFrom,
  ] = useState("")

  const [
    dateTo,
    setDateTo,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // FILTER PAYLOAD
  // =======================================================

  const buildFilters =
    () => {
      return {
        expense_type:
          typeFilter,

        status:
          statusFilter,

        date_from:
          dateFrom,

        date_to:
          dateTo,
      }
    }


  // =======================================================
  // LOAD EXPENSES
  // =======================================================

  const loadExpenses =
    async (
      filters = {}
    ) => {
      try {
        setLoading(true)

        const data =
          await getExpenses(
            filters
          )

        setExpenses(
          Array.isArray(data)
            ? data
            : []
        )

        setPaginationModel({
          page: 0,
          pageSize:
            ROWS_PER_PAGE,
        })
      } catch (error) {
        console.error(
          "Error loading expenses:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Erreur lors du chargement des dépenses."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getExpenseFormData()

        setStores(
          Array.isArray(
            data?.stores
          )
            ? data.stores
            : []
        )

        setUsers(
          Array.isArray(
            data?.users
          )
            ? data.users
            : []
        )

        setExpenseTypes(
          Array.isArray(
            data?.expense_types
          )
            ? data.expense_types
            : []
        )

        setStatuses(
          Array.isArray(
            data?.statuses
          )
            ? data.statuses
            : []
        )
      } catch (error) {
        console.error(
          "Error loading expense form data:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Erreur lors du chargement du formulaire."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadExpenses()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // TOTALS
  // =======================================================

  const totals =
    useMemo(() => {
      const confirmed =
        expenses.filter(
          (item) =>
            item.status ===
            "confirmed"
        )

      const pending =
        expenses.filter(
          (item) =>
            item.status ===
            "pending"
        )

      const cancelled =
        expenses.filter(
          (item) =>
            item.status ===
            "cancelled"
        )

      return {
        count:
          expenses.length,

        confirmedAmount:
          confirmed.reduce(
            (
              sum,
              item
            ) =>
              sum +
              toNumber(
                item.amount
              ),
            0
          ),

        pendingAmount:
          pending.reduce(
            (
              sum,
              item
            ) =>
              sum +
              toNumber(
                item.amount
              ),
            0
          ),

        cancelledAmount:
          cancelled.reduce(
            (
              sum,
              item
            ) =>
              sum +
              toNumber(
                item.amount
              ),
            0
          ),
      }
    }, [
      expenses,
    ])


  // =======================================================
  // FORM
  // =======================================================

  const buildDefaultForm =
    () => {
      const base = {
        ...defaultFormData,

        company_id:
          session.companyId ||
          1,

        expense_date:
          getNowForInput(),

        created_by:
          session.userId ||
          "",
      }

      if (
        shopUser
      ) {
        base.store_id =
          session.storeId ||
          ""

        base.user_id =
          session.userId ||
          ""

        base.created_by =
          session.userId ||
          ""
      }

      return base
    }


  const openAddDialog =
    () => {
      setSelectedExpense(
        null
      )

      setFormData(
        buildDefaultForm()
      )

      setOpen(true)
    }


  const openEditDialog =
    async (
      expenseId
    ) => {
      try {
        const expense =
          await getExpenseById(
            expenseId
          )

        setSelectedExpense(
          expense
        )

        setFormData({
          company_id:
            expense.company_id ||
            session.companyId ||
            1,

          store_id:
            expense.store_id ||
            "",

          user_id:
            expense.user_id ||
            "",

          expense_date:
            formatDateForInput(
              expense.expense_date
            ),

          expense_type:
            expense.expense_type ||
            "",

          amount:
            expense.amount !==
              null &&
            expense.amount !==
              undefined
              ? Number(
                  expense.amount
                )
              : "",

          description:
            expense.description ||
            "",

          status:
            expense.status ||
            "confirmed",

          external_reference:
            expense.external_reference ||
            "",

          created_by:
            expense.created_by ||
            session.userId ||
            "",
        })

        setOpen(true)
      } catch (error) {
        console.error(
          "Error loading expense:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger la dépense."
          ),
          "error"
        )
      }
    }


  const closeDialog =
    () => {
      if (
        saving
      ) {
        return
      }

      setOpen(false)

      setSelectedExpense(
        null
      )

      setFormData({
        ...defaultFormData,
      })
    }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      setFormData(
        (previous) => {
          if (
            [
              "company_id",
              "store_id",
              "user_id",
              "created_by",
            ].includes(name)
          ) {
            return {
              ...previous,

              [name]:
                value === ""
                  ? ""
                  : Number(value),
            }
          }

          if (
            name ===
            "amount"
          ) {
            return {
              ...previous,

              [name]:
                value === ""
                  ? ""
                  : Number(value),
            }
          }

          return {
            ...previous,
            [name]: value,
          }
        }
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm =
    () => {
      if (
        !shopUser &&
        !formData.store_id
      ) {
        showSnackbar(
          "Veuillez sélectionner le shop concerné.",
          "warning"
        )

        return false
      }


      if (
        !shopUser &&
        !formData.user_id
      ) {
        showSnackbar(
          "Veuillez sélectionner l'utilisateur concerné.",
          "warning"
        )

        return false
      }


      if (
        !formData.expense_type
      ) {
        showSnackbar(
          "Veuillez sélectionner le type de dépense.",
          "warning"
        )

        return false
      }


      if (
        formData.amount ===
          "" ||
        Number(
          formData.amount
        ) <= 0
      ) {
        showSnackbar(
          "Le montant doit être supérieur à 0.",
          "warning"
        )

        return false
      }

      return true
    }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          Number(
            formData.company_id ||
              session.companyId ||
              1
          ),

        store_id:
          formData.store_id
            ? Number(
                formData.store_id
              )
            : null,

        user_id:
          formData.user_id
            ? Number(
                formData.user_id
              )
            : null,

        expense_date:
          formData.expense_date ||
          null,

        expense_type:
          formData.expense_type,

        amount:
          Number(
            formData.amount
          ),

        description:
          formData.description
            ?.trim() ||
          null,

        status:
          formData.status ||
          "confirmed",

        external_reference:
          formData.external_reference
            ?.trim() ||
          null,

        created_by:
          formData.created_by
            ? Number(
                formData.created_by
              )
            : session.userId ||
              null,
      }

      try {
        setSaving(true)

        if (
          selectedExpense?.id
        ) {
          await updateExpense(
            selectedExpense.id,
            payload
          )

          showSnackbar(
            "Dépense modifiée avec succès."
          )
        } else {
          await createExpense(
            payload
          )

          showSnackbar(
            "Dépense enregistrée avec succès."
          )
        }

        /*
         * Fermeture directe :
         * closeDialog() est volontairement évité ici,
         * car il bloque lorsque saving === true.
         */
        setOpen(false)

        setSelectedExpense(
          null
        )

        setFormData({
          ...defaultFormData,
        })

        await loadExpenses(
          buildFilters()
        )
      } catch (error) {
        console.error(
          "Error saving expense:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Erreur lors de l'enregistrement."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (
      expenseId
    ) => {
      const confirmed =
        window.confirm(
          "Voulez-vous vraiment supprimer cette dépense ?"
        )

      if (
        !confirmed
      ) {
        return
      }

      try {
        await deleteExpense(
          expenseId
        )

        showSnackbar(
          "Dépense supprimée avec succès."
        )

        await loadExpenses(
          buildFilters()
        )
      } catch (error) {
        console.error(
          "Error deleting expense:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Erreur lors de la suppression."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // FILTER ACTIONS
  // =======================================================

  const applyFilters =
    async () => {
      await loadExpenses(
        buildFilters()
      )
    }


  const resetFilters =
    async () => {
      setSearch("")
      setTypeFilter("")
      setStatusFilter("")
      setDateFrom("")
      setDateTo("")

      setPaginationModel({
        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })

      await loadExpenses()
    }


  // =======================================================
  // CLIENT SEARCH
  // =======================================================

  const filteredExpenses =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return expenses.filter(
        (expense) => {
          if (
            !keyword
          ) {
            return true
          }

          return [
            expense.expense_code,
            expense.store_name,
            expense.organization_name,
            expense.user_name,
            expense.expense_type,
            getExpenseTypeLabel(
              expense.expense_type
            ),
            expense.description,
            expense.external_reference,
            getStatusConfig(
              expense.status
            ).label,
          ].some(
            (value) =>
              String(
                value || ""
              )
                .toLowerCase()
                .includes(
                  keyword
                )
          )
        }
      )
    }, [
      expenses,
      search,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredExpenses.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredExpenses,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredExpenses.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredExpenses.length,
    paginationModel.page,
  ])


  // =======================================================
  // SELECTED LABELS
  // =======================================================

  const selectedStoreLabel =
    useMemo(() => {
      if (
        shopUser
      ) {
        const code =
          session.storeCode ||
          ""

        const name =
          session.storeName ||
          ""

        if (
          code ||
          name
        ) {
          return `${code} - ${name}`.trim()
        }

        return "Shop assigné"
      }

      const store =
        stores.find(
          (item) =>
            Number(
              item.id
            ) ===
            Number(
              formData.store_id
            )
        )

      if (
        !store
      ) {
        return ""
      }

      return `${store.code} - ${store.name}`
    }, [
      shopUser,
      session.storeCode,
      session.storeName,
      stores,
      formData.store_id,
    ])


  const selectedUserLabel =
    useMemo(() => {
      if (
        shopUser
      ) {
        return (
          session.fullName ||
          "Utilisateur connecté"
        )
      }

      const user =
        users.find(
          (item) =>
            Number(
              item.id
            ) ===
            Number(
              formData.user_id
            )
        )

      if (
        !user
      ) {
        return ""
      }

      return (
        user.label ||
        user.full_name ||
        `Utilisateur ${user.id}`
      )
    }, [
      shopUser,
      session.fullName,
      users,
      formData.user_id,
    ])


  // =======================================================
  // COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "expense",

          headerName:
            "Dépense",

          minWidth: 225,

          flex: 1.35,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.16}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.expense_code ||
                    ""
                  }
                >
                  {params.row.expense_code ||
                    `DEP-${params.row.id}`}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.description ||
                    ""
                  }
                >
                  {params.row.description ||
                    "Aucune description"}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "store",

          headerName:
            shopUser
              ? "Shop"
              : "Shop / organisation",

          minWidth: 210,

          flex: 1.2,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.14}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#334155",
                    fontSize: "0.83rem",
                    fontWeight: 800,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.store_name ||
                    ""
                  }
                >
                  {params.row.store_name ||
                    "-"}
                </Typography>

                {!shopUser && (
                  <Typography
                    variant="caption"

                    sx={{
                      color: "#64748b",
                      fontWeight: 700,
                      lineHeight: 1.15,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}

                    title={
                      params.row.organization_name ||
                      ""
                    }
                  >
                    {params.row.organization_name ||
                      "Organisation non renseignée"}
                  </Typography>
                )}
              </Stack>
            ),
        },


        {
          field:
            "user_name",

          headerName:
            "Utilisateur",

          minWidth: 150,

          flex: 0.9,

          renderCell:
            (params) => (
              <Typography
                sx={{
                  width: "100%",
                  color: "#475569",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}

                title={
                  params.value ||
                  ""
                }
              >
                {params.value ||
                  "-"}
              </Typography>
            ),
        },


        {
          field:
            "expense_type",

          headerName:
            "Type",

          width: 145,

          renderCell:
            (params) => (
              <Chip
                size="small"
                variant="outlined"
                label={getExpenseTypeLabel(
                  params.value
                )}
                sx={{
                  maxWidth: 130,

                  "& .MuiChip-label":
                    {
                      overflow:
                        "hidden",

                      textOverflow:
                        "ellipsis",
                    },
                }}
              />
            ),
        },


        {
          field: "amount",

          headerName:
            "Montant",

          width: 125,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => (
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "right",

                  color: "#0f172a",

                  fontSize: "0.87rem",

                  fontWeight: 900,
                }}
              >
                {formatAmount(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field: "status",

          headerName:
            "Statut",

          width: 120,

          renderCell:
            (params) =>
              getStatusChip(
                params.value
              ),
        },


        {
          field:
            "expense_date",

          headerName:
            "Date",

          width: 135,

          renderCell:
            (params) => (
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: "0.79rem",
                  fontWeight: 700,
                  lineHeight: 1.25,
                }}
              >
                {formatDate(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field: "actions",

          headerName:
            "Actions",

          width:
            shopUser
              ? 100
              : 185,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Stack
                direction="row"

                spacing={0.55}

                sx={{
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  disableElevation

                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 78,
                    px: 1,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Modifier
                </Button>


                {!shopUser && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"

                    onClick={() =>
                      handleDelete(
                        params.row.id
                      )
                    }

                    sx={{
                      minWidth: 82,
                      px: 0.9,
                      whiteSpace:
                        "nowrap",
                    }}
                  >
                    Supprimer
                  </Button>
                )}
              </Stack>
            ),
        },
      ],
      [
        shopUser,
      ]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Dépenses magasin
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 820,
              }}
            >
              {shopUser
                ? `Dépenses enregistrées pour votre shop : ${
                    session.storeName ||
                    "shop assigné"
                  }.`
                : organizationUser
                ? `Dépenses des shops de ${
                    session.organizationName ||
                    "votre organisation"
                  }.`
                : adminUser
                ? "Vue globale des dépenses par shop et organisation."
                : "Gestion des dépenses opérationnelles."}
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation

            onClick={
              openAddDialog
            }

            sx={{
              minWidth: {
                xs: "100%",
                sm: 180,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouvelle dépense
          </Button>
        </Box>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Confirmées"

            value={
              formatAmount(
                totals.confirmedAmount
              )
            }

            caption="Montant confirmé"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <ReceiptLongRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="En attente"

            value={
              formatAmount(
                totals.pendingAmount
              )
            }

            caption="Montant à confirmer"

            accent="#d97706"

            background="rgba(217, 119, 6, 0.09)"

            icon={
              <ScheduleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Annulées"

            value={
              formatAmount(
                totals.cancelledAmount
              )
            }

            caption="Montant annulé"

            accent="#dc2626"

            background="rgba(220, 38, 38, 0.09)"

            icon={
              <ErrorRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Dépenses"

            value={
              totals.count
            }

            caption="Lignes chargées"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 220px 190px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Code, shop, utilisateur, type, description..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"

              label="Type"

              value={
                typeFilter
              }

              onChange={(
                event
              ) =>
                setTypeFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {expenseTypes.map(
                (type) => (
                  <MenuItem
                    key={
                      type.value
                    }

                    value={
                      type.value
                    }
                  >
                    {type.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              fullWidth
              size="small"

              label="Statut"

              value={
                statusFilter
              }

              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {statuses.map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }

                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>
          </Box>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "180px 180px minmax(280px, 1fr)",
              },

              gap: 1,

              alignItems: "center",

              mt: 1,
            }}
          >
            <TextField
              type="date"
              size="small"

              label="Du"

              value={
                dateFrom
              }

              onChange={(
                event
              ) =>
                setDateFrom(
                  event.target.value
                )
              }

              InputLabelProps={{
                shrink: true,
              }}

              fullWidth
            />


            <TextField
              type="date"
              size="small"

              label="Au"

              value={
                dateTo
              }

              onChange={(
                event
              ) =>
                setDateTo(
                  event.target.value
                )
              }

              InputLabelProps={{
                shrink: true,
              }}

              fullWidth
            />


            <Box
              sx={{
                gridColumn: {
                  xs: "1 / -1",
                  sm: "1 / -1",
                  lg: "auto",
                },

                display: "flex",

                flexDirection: {
                  xs: "column",
                  sm: "row",
                },

                justifyContent:
                  "flex-end",

                gap: 0.8,

                minWidth: 0,
              }}
            >
              <Button
                variant="contained"
                disableElevation

                onClick={
                  applyFilters
                }

                disabled={
                  loading
                }

                sx={{
                  width: {
                    xs: "100%",
                    sm: 125,
                  },

                  minWidth: {
                    xs: "100%",
                    sm: 125,
                  },

                  flexShrink: 0,

                  whiteSpace:
                    "nowrap",
                }}
              >
                Appliquer
              </Button>


              <Button
                variant="outlined"

                onClick={
                  resetFilters
                }

                disabled={
                  loading
                }

                sx={{
                  width: {
                    xs: "100%",
                    sm: 140,
                  },

                  minWidth: {
                    xs: "100%",
                    sm: 140,
                  },

                  flexShrink: 0,

                  whiteSpace:
                    "nowrap",
                }}
              >
                Réinitialiser
              </Button>
            </Box>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des dépenses
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Dépenses opérationnelles par shop, utilisateur,
                type et statut
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredExpenses.length} dépense${
                filteredExpenses.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth:
                    shopUser
                      ? 1050
                      : 1160,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredExpenses
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={72}

                  columnHeaderHeight={50}

                  getRowClassName={(
                    params
                  ) =>
                    getExpenseRowClass(
                      params.row.status
                    )
                  }

                  localeText={{
                    noRowsLabel:
                      "Aucune dépense",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    fontSize: "0.83rem",

                    bgcolor:
                      "#ffffff",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1.25,

                        borderRight:
                          "1px solid rgba(226, 232, 240, 0.60)",
                      },

                    "& .MuiDataGrid-columnHeader:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        color:
                          "#334155",

                        fontSize:
                          "0.76rem",

                        fontWeight: 900,

                        letterSpacing:
                          "0.025em",
                      },

                    "& .MuiDataGrid-columnSeparator":
                      {
                        color:
                          "rgba(148, 163, 184, 0.18)",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1.25,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderBottom:
                          "1px solid #edf2f7",

                        borderRight:
                          "1px solid rgba(241, 245, 249, 0.85)",
                      },

                    "& .MuiDataGrid-cell:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .expense-row":
                      {
                        bgcolor:
                          "#ffffff",

                        transition:
                          "background-color 0.16s ease, box-shadow 0.16s ease",
                      },

                    "& .expense-row--confirmed":
                      {
                        boxShadow:
                          "inset 4px 0 0 #22c55e",

                        bgcolor:
                          "rgba(240, 253, 244, 0.28)",
                      },

                    "& .expense-row--pending":
                      {
                        boxShadow:
                          "inset 4px 0 0 #f59e0b",

                        bgcolor:
                          "rgba(255, 251, 235, 0.34)",
                      },

                    "& .expense-row--cancelled":
                      {
                        boxShadow:
                          "inset 4px 0 0 #ef4444",

                        bgcolor:
                          "rgba(254, 242, 242, 0.28)",
                      },

                    "& .expense-row--default":
                      {
                        boxShadow:
                          "inset 4px 0 0 #cbd5e1",
                      },

                    "& .expense-row:hover":
                      {
                        bgcolor:
                          "#f8fbff !important",

                        boxShadow:
                          "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 52,

                        bgcolor:
                          "#fbfdff",

                        borderTop:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 52,

                        px: 1.25,
                      },

                    "& .MuiTablePagination-displayedRows":
                      {
                        color:
                          "#475569",

                        fontSize:
                          "0.78rem",

                        fontWeight: 800,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },

                    "& .MuiDataGrid-virtualScroller":
                      {
                        overflowX:
                          "auto",
                      },

                    "& .MuiDataGrid-scrollbar--horizontal":
                      {
                        height:
                          "8px",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (

            /* =================================================
               MOBILE / TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des dépenses...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune dépense trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map(
                    (row) => (
                      <ExpenseMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }

                        shopUser={
                          shopUser
                        }

                        onEdit={
                          openEditDialog
                        }

                        onDelete={
                          handleDelete
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredExpenses.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          EXPENSE DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          closeDialog
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedExpense
              ? "Modifier la dépense"
              : "Nouvelle dépense"}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedExpense
              ? "Modifiez les informations de cette dépense opérationnelle."
              : "Enregistrez une dépense liée à un shop et à un utilisateur."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >
            {shopUser && (
              <Alert
                severity="info"
                variant="outlined"
              >
                Vous êtes connecté comme gérant. Le shop et
                l’utilisateur sont verrouillés automatiquement
                selon votre compte.
              </Alert>
            )}


            {/* AFFECTATION */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Affectation
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                {shopUser ? (
                  <TextField
                    size="small"

                    label="Shop"

                    value={
                      selectedStoreLabel
                    }

                    fullWidth

                    disabled

                    helperText="Shop verrouillé selon votre compte utilisateur"
                  />
                ) : (
                  <TextField
                    select
                    size="small"

                    label="Shop"

                    name="store_id"

                    value={
                      formData.store_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {stores.map(
                      (store) => (
                        <MenuItem
                          key={
                            store.id
                          }

                          value={
                            store.id
                          }
                        >
                          {store.code} -{" "}
                          {store.name}
                          {store.organization_name
                            ? ` • ${store.organization_name}`
                            : ""}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}


                {shopUser ? (
                  <TextField
                    size="small"

                    label="Créé par"

                    value={
                      selectedUserLabel
                    }

                    fullWidth

                    disabled

                    helperText="Utilisateur connecté"
                  />
                ) : (
                  <TextField
                    select
                    size="small"

                    label="Créé par / Utilisateur"

                    name="user_id"

                    value={
                      formData.user_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {users.map(
                      (user) => (
                        <MenuItem
                          key={
                            user.id
                          }

                          value={
                            user.id
                          }
                        >
                          {user.label ||
                            user.full_name ||
                            `Utilisateur ${user.id}`}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              </Box>
            </Paper>


            {/* DETAILS */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Détails de la dépense
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  type="datetime-local"

                  size="small"

                  label="Date dépense"

                  name="expense_date"

                  value={
                    formData.expense_date
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  InputLabelProps={{
                    shrink: true,
                  }}
                />


                <TextField
                  select
                  size="small"

                  label="Type de dépense"

                  name="expense_type"

                  value={
                    formData.expense_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }
                >
                  {expenseTypes.map(
                    (type) => (
                      <MenuItem
                        key={
                          type.value
                        }

                        value={
                          type.value
                        }
                      >
                        {type.label}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  size="small"

                  label="Montant"

                  name="amount"

                  type="number"

                  value={
                    formData.amount
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />


                <TextField
                  select
                  size="small"

                  label="Statut"

                  name="status"

                  value={
                    formData.status
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {statuses.map(
                    (status) => (
                      <MenuItem
                        key={
                          status.value
                        }

                        value={
                          status.value
                        }
                      >
                        {status.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>


              <TextField
                size="small"

                label="Référence externe"

                name="external_reference"

                value={
                  formData.external_reference
                }

                onChange={
                  handleChange
                }

                fullWidth

                disabled={
                  saving
                }

                placeholder="Numéro reçu, référence paiement, note interne..."
              />
            </Paper>


            {/* DESCRIPTION */}

            <TextField
              label="Description"

              name="description"

              value={
                formData.description
              }

              onChange={
                handleChange
              }

              fullWidth

              multiline

              minRows={3}

              disabled={
                saving
              }

              placeholder="Exemple : Loyer shop Triangle, transport, carburant..."
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              closeDialog
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedExpense
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default ExpensesPage
